import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ZoneErrors} from '@domain/production-schedule/zone-errors';
import {ZoneWarnings} from '@domain/production-schedule/zone-warnings';
import {ZoneType} from '@domain/zone-type.enum';
import {PRODUCTION_SCHEDULES, ProductionSchedules} from '@infrastructure/http/production-schedule/production-schedules';
import {Zone} from '@presentation/pages/texfab/production-schedule/add/plan/builder/zone';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {AssertionUtils, BaseComponent, canShowErrorForErrorCodeAndFormControlForFormGroup, Message, MessageType, TranslateService} from '@vdw/angular-component-library';
import {cloneDeep, keys} from 'lodash-es';
import {debounceTime, distinctUntilChanged, filter, finalize, takeUntil, tap} from 'rxjs/operators';

@Component({
  selector: 'app-edit-zone',
  templateUrl: './edit-zone.component.html',
  styleUrls: ['./edit-zone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditZoneComponent extends BaseComponent implements OnInit {
  public editZoneForm: FormGroup<{technicalWidthInDents: FormControl<number>}>;
  public isVerifying = false;
  public unusedDents = 0;
  public maxAllowed = 0;
  public readonly zoneReference: Zone;
  public readonly suggestedZone: Zone;

  private zone: Zone;
  private productionSchedule: ProductionSchedule;
  private readonly zoneIndex: number;
  private readonly zoneType: ZoneType;

  public constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject(PRODUCTION_SCHEDULES) private readonly productionSchedules: ProductionSchedules,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly translate: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly dialogRef: MatDialogRef<EditZoneComponent>,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    super();
    const {suggestedZone, zone, productionSchedule, zoneIndex, zoneType} = data;
    this.zoneType = zoneType;
    this.suggestedZone = suggestedZone;
    this.productionSchedule = cloneDeep(productionSchedule);
    this.zone = zone;
    this.zoneIndex = zoneIndex;
    this.zoneReference = this.getZone(this.productionSchedule);
  }

  public ngOnInit(): void {
    this.setUnusedDents();
    this.setFormFields();
  }

  public canEditZone(): boolean {
    return this.editZoneForm.valid && this.productionSchedule.canBeModified();
  }

  public canShowErrorForFormControl(errorCode: string, formControlName: string): boolean {
    return canShowErrorForErrorCodeAndFormControlForFormGroup(errorCode, formControlName, this.editZoneForm);
  }

  public getLabelTranslation(): string {
    let result = 'PRODUCTION_ORDER.WEAVE_PLAN';

    if (this.zoneType === ZoneType.FREE) {
      result = `${result}.FREE_ZONE.FREE_ZONE`;
    } else if (this.zoneType === ZoneType.REST) {
      result = `${result}.REST_ZONE.REST_ZONE`;
    }

    return this.translate.instant(result, {count: 1});
  }

  public getIcon(): string {
    if (this.canShowErrorMessages()) {
      return 'error-full-36px';
    }

    if (this.canShowWarningMessages()) {
      return 'warning-full-36px';
    }

    return 'info-blue';
  }

  public canShowErrorMessages(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.zone.errors);
  }

  public canShowWarningMessages(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.zone.warnings);
  }

  public getWarningMessages(): Message[] {
    return this.getMessagesForZoneWarningsOrErrors(this.zone.warnings, MessageType.WARNING);
  }

  public getErrorMessages(): Message[] {
    return this.getMessagesForZoneWarningsOrErrors(this.zone.errors, MessageType.ERROR);
  }

  public close(): void {
    if (!this.isVerifying) {
      this.dialogRef.close();
    }
  }

  private getZone(productionSchedule: ProductionSchedule): Zone {
    let result: Zone;

    if (this.zoneType === ZoneType.FREE) {
      result = productionSchedule.freeZones[this.zoneIndex];
    } else if (this.zoneType === ZoneType.REST) {
      result = productionSchedule.restZones[this.zoneIndex];
    }

    return result;
  }

  private setFormFields(): void {
    let productionSchedulePathsWidthInDents = 0;
    this.productionSchedule.productionSchedulePathsOfColoredYarnSets
      .flatMap((path: ProductionSchedulePathsOfColoredYarnSet) => path.productionSchedulePaths)
      .forEach((path: ProductionSchedulePath) => (productionSchedulePathsWidthInDents += path.technicalWidthInDents));

    this.maxAllowed = this.productionSchedule.machine.dentsForFabric - productionSchedulePathsWidthInDents;

    this.editZoneForm = this.formBuilder.group({
      technicalWidthInDents: [{value: this.zoneReference.technicalWidthInDents, disabled: !this.productionSchedule.canBeModified()}, [Validators.required, Validators.max(this.maxAllowed)]]
    });

    if (this.productionSchedule.canBeModified()) {
      this.editZoneForm.controls.technicalWidthInDents.valueChanges
        .pipe(
          tap(() => this.editZoneForm.controls.technicalWidthInDents.markAsTouched()),
          debounceTime(500),
          distinctUntilChanged(),
          filter((technicalWidthInDents: number) => !AssertionUtils.isNullOrUndefined(technicalWidthInDents) && technicalWidthInDents <= this.maxAllowed),
          takeUntil(this.unSubscribeOnViewDestroy)
        )
        .subscribe((technicalWidthInDents: number) => {
          this.zoneReference.technicalWidthInDents = technicalWidthInDents;
          this.checkVerifyProductionSchedule();
        });
    }
  }

  private getDifferenceBetweenOldAndNewTechnicalWidthInDents(): number {
    return this.editZoneForm.getRawValue().technicalWidthInDents - this.zone.technicalWidthInDents;
  }

  private getZoneErrorOrWarningTranslationKey(key: string): string {
    return {
      totalWidthTooBig: 'ERRORS.TOTAL_WIDTH_TOO_BIG',
      totalWidthTooLittle: 'WARNINGS.TOTAL_WIDTH_TOO_LITTLE',
      freeZoneFixedWidthInDents: 'WARNINGS.FREE_ZONE_FIXED_WIDTH_IN_DENTS',
      restZoneFixedWidthInDents: 'WARNINGS.REST_ZONE_FIXED_WIDTH_IN_DENTS',
      freeZoneUseAsMaximumExceeded: 'ERRORS.FREE_ZONE_USE_AS_MAXIMUM_EXCEEDED',
      pathStartedAtWrongDent: 'ERRORS.PATH_STARTED_AT_WRONG_DENT',
      userDefinedRestAdjusted: 'WARNINGS.USER_DEFINED_REST_ADJUSTED'
    }[key];
  }

  private checkVerifyProductionSchedule(): void {
    this.isVerifying = true;
    this.dialogRef.disableClose = true;
    this.changeDetectorRef.detectChanges();

    this.productionSchedules
      .checkVerifyProductionSchedule(
        this.productionSchedule.id as string,
        this.zoneType,
        this.zone.startDent,
        this.getDifferenceBetweenOldAndNewTechnicalWidthInDents(),
        this.zone.coloredYarnSetPathNr
      )
      .pipe(
        finalize(() => {
          this.isVerifying = false;
          this.dialogRef.disableClose = false;
          this.changeDetectorRef.detectChanges();
        }),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe((productionSchedule: ProductionSchedule) => {
        this.productionSchedule = ProductionSchedule.modifyProductionScheduleAfterVerify(this.productionSchedule, productionSchedule);
        this.productionSchedulePlan.dispatchNewProductionSchedule(this.productionSchedule);

        this.zone = this.getZone(this.productionSchedule);
        this.productionSchedulePlan.setSelectedZone(this.zone);

        this.setUnusedDents();
      });
  }

  private getMessagesForZoneWarningsOrErrors(zoneWarningsOrErrors: ZoneWarnings | ZoneErrors, type: MessageType): Message[] {
    return keys(zoneWarningsOrErrors).reduce((messages: Message[], key: string) => {
      const value = zoneWarningsOrErrors[key];

      if (!AssertionUtils.isNullOrUndefined(value)) {
        let description: string = this.getZoneErrorOrWarningTranslationKey(key);

        if (!AssertionUtils.isNullOrUndefined(description)) {
          description = this.translate.instant(`PRODUCTION_ORDER.${description}`, {value});
        }

        messages.push(Message.createMessageWithTypeAndDescription(type, description));
      }

      return messages;
    }, []);
  }

  private setUnusedDents(): void {
    this.unusedDents = this.productionSchedule.productionSchedulePathsOfColoredYarnSets[this.zone.coloredYarnSetPathNr - 1].errors?.totalWidthTooLittle ?? 0;
  }
}
